<script setup lang="ts">
import { ref, watch } from "vue";
import { injectStyle } from "@zyzgroup/core-web";
import RadioGroup from "../form/RadioGroup.vue";
import CheckboxGroup from "../form/CheckboxGroup.vue";
import InputRange from "../form/InputRange.vue";

// for merge ce shadow-root styles
defineOptions({
  customOptions: { RadioGroup, CheckboxGroup, InputRange },
  inheritAttrs: true
});

const props = withDefaults(
  defineProps<{
    width?: number;
    height?: number;
    dur?: number;
    perspective?: number; // perspective 定义摄像机（也就是作为观众的我们）到屏幕的距离 : 100px ~ 2000px | none
  }>(),
  { width: 200, height: 200, dur: 1000, perspective: 400 }
);

const emits = defineEmits<{
  // (event: "click", isFlipped: boolean): void;
}>();

const hW = ref(Math.floor(props.width / 2));
// const hH = ref(Math.floor(props.height / 2));
const styleSheet = `
  :root {
    --spin-cube-z: -${hW.value}px;
  }
  :host {
    --spin-cube-z: -${hW.value}px;
  }
`;
const container = ref();
watch(container, () => {
  if (!container.value) {
    return;
  }
  injectStyle(styleSheet, container.value);
});

/// perspective: 400px;
const p = ref(props.perspective + "px");
const perspectiveChange = (v: number) => {
  p.value = v == 2000 ? "none" : v + "px";
};

/// perspective-origin 定义摄像机观察到的画面中的灭点（vanishing point）的位置
/// perspective-origin: 50% 50%;
const pox = ref("50%");
const perspectiveOriginXChange = (v: number) => {
  pox.value = v + "%";
};
const poy = ref("50%");
const perspectiveOriginYChange = (v: number) => {
  poy.value = v + "%";
};

/// cube transform
const b = `translateZ(-${hW.value}px)`;
const cubeT = ref(b);
const sideChange = (side: string) => {
  switch (side) {
    case "front":
      cubeT.value = b + " rotateY(0deg)";
      break;
    case "right":
      cubeT.value = b + " rotateY(-90deg)";
      break;
    case "back":
      cubeT.value = b + " rotateY(-180deg)";
      break;
    case "left":
      cubeT.value = b + " rotateY(90deg)";
      break;
    case "top":
      cubeT.value = b + " rotateX(-90deg)";
      break;
    case "bottom":
      cubeT.value = b + " rotateX(90deg)";
      break;
    default:
      cubeT.value = b;
      break;
  }
};

/// animation: spinCube 8s infinite ease-in-out;
const cubeA = ref(false);
const spinCubeChange = (a: string[]) => {
  if (a.length) {
    cubeA.value = true;
  } else {
    cubeA.value = false;
  }
};

/// backface-visibility: hidden;
const bv = ref<"visible" | "hidden">("visible");
const backVisibleChange = (a: string[]) => {
  if (a.length) {
    bv.value = "visible";
  } else {
    bv.value = "hidden";
  }
};

defineExpose({});
</script>

<template>
  <div ref="container">
    <div
      class="cube-scene"
      :style="{
        width: `${props.width}px`,
        height: `${props.height}px`,
        perspective: `${p}`,
        perspectiveOrigin: `${pox} ${poy}`
      }"
    >
      <div
        class="cube"
        :class="{ spinning: cubeA }"
        :style="{
          transformStyle: `preserve-3d`,
          transform: cubeT,
          transition: `transform ${props.dur}ms`
        }"
      >
        <div
          class="face front"
          :style="{
            transform: `rotateY(0deg) translateZ(${hW}px)`,
            backfaceVisibility: bv
          }"
        >
          <slot name="front">front</slot>
        </div>
        <div
          class="face back"
          :style="{
            transform: `rotateY(180deg) translateZ(${hW}px)`,
            backfaceVisibility: bv
          }"
        >
          <slot name="back">back</slot>
        </div>
        <div
          class="face right"
          :style="{
            transform: `rotateY(90deg) translateZ(${hW}px)`,
            backfaceVisibility: bv
          }"
        >
          <slot name="right">right</slot>
        </div>
        <div
          class="face left"
          :style="{
            transform: `rotateY(-90deg) translateZ(${hW}px)`,
            backfaceVisibility: bv
          }"
        >
          <slot name="left">left</slot>
        </div>
        <div
          class="face top"
          :style="{
            transform: `rotateX(90deg) translateZ(${hW}px)`,
            backfaceVisibility: bv
          }"
        >
          <slot name="top">top</slot>
        </div>
        <div
          class="face bottom"
          :style="{
            transform: `rotateX(-90deg) translateZ(${hW}px)`,
            backfaceVisibility: bv
          }"
        >
          <slot name="bottom">bottom</slot>
        </div>
      </div>
    </div>
    <div>
      <RadioGroup
        :options="['front', 'right', 'back', 'left', 'top', 'bottom']"
        @change="sideChange"
      ></RadioGroup>
    </div>
    <div>
      <CheckboxGroup
        :options="['Backface visible']"
        @change="backVisibleChange"
      ></CheckboxGroup>
    </div>
    <div>
      <CheckboxGroup
        :options="['spin cube']"
        @change="spinCubeChange"
      ></CheckboxGroup>
    </div>
    <div>
      perspective:
      <InputRange
        :min="1"
        :max="2000"
        :defaultValue="props.perspective"
        unit="px"
        @change="perspectiveChange"
      ></InputRange>
    </div>
    <div>
      perspective-origin x:
      <InputRange
        :min="0"
        :max="100"
        :defaultValue="50"
        unit="%"
        @change="perspectiveOriginXChange"
      ></InputRange>
    </div>
    <div>
      perspective-origin y:
      <InputRange
        :min="0"
        :max="100"
        :defaultValue="50"
        unit="%"
        @change="perspectiveOriginYChange"
      ></InputRange>
    </div>
  </div>
</template>

<!--
  ce 中不要使用v-bind
  border: 1px solid v-bind(borderColor);
-->
<style scoped>
.cube {
  position: relative;
  width: 100%;
  height: 100%;
}
.face {
  position: absolute;
  width: 100%;
  height: 100%;
}
.front {
  background: hsla(0, 100%, 50%, 0.7);
}
.right {
  background: hsla(60, 100%, 50%, 0.7);
}
.back {
  background: hsla(120, 100%, 50%, 0.7);
}
.left {
  background: hsla(180, 100%, 50%, 0.7);
}
.top {
  background: hsla(240, 100%, 50%, 0.7);
}
.bottom {
  background: hsla(300, 100%, 50%, 0.7);
}

.spinning {
  animation: spinCube 8s infinite ease-in-out;
}
@keyframes spinCube {
  0% {
    transform: translateZ(var(--spin-cube-z)) rotateX(0deg) rotateY(0deg);
  }
  100% {
    transform: translateZ(var(--spin-cube-z)) rotateX(360deg) rotateY(360deg);
  }
}
</style>
